import type { FC } from 'react';
import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { parseFloatOrFallback } from '@wix/da-shared-react/pkg/EarningsCalculator';
import { useStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import Flex from '@wix/da-ds/pkg/Flex';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  PureBiData,
  StudioEarningsWithdrawalClickBiEvent,
  StudioWithdrawalPaymentMethod,
} from '@wix/da-bi/pkg/events';
import {
  getAvailableBalance,
  getPendingTransactions,
  getTotalWithdrawn,
  getIsTwofactorRequired,
  getIsWithdrawalSuspended,
} from '../../../selectors/earnings';
import DollarSign from '@wix/da-ds/pkg/Icons/32x32/DollarSign';
import StudioAnalytics from '@wix/da-ds/pkg/Icons/32x32/StudioAnalytics';
import StudioScheduled from '@wix/da-ds/pkg/Icons/24x24/StudioScheduled';
import Reports from '@wix/da-ds/pkg/Icons/16x16/Reports';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import TransactionsTable from '../TransactionsTable';
import EmptyEarningsList from '../EmptyEarningsList';
import WithdrawAsPointsModal from './modals/WithdrawAsPointsModal';
import PayPalWithdrawRootModal from './modals/PayPalWithdrawRootModal/PayPalWithdrawRootModal';
import StripeWithdrawRootModal from './modals/StripeWithdrawRootModal';
import BitPayWithdrawRootModal from './modals/BitPayWithdrawRootModal';
import TwoFactorRequiredModal from './modals/TwoFactorRequiredModal';
import WithdrawalSuspendedModal from './modals/WithdrawalSuspendedModal';
import { useOpenStripeModalFromQueryParam } from './hooks';
import { URLS } from '@wix/da-url';
import { STUDIO_EARNINGS_TRANSACTIONS_STREAM } from '../../../../constants';

import s from './EarningsPageContent.scss';

export interface Props {
  className?: string;
}

const EarningsPageContent: FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const logEvent = useBiLogger();
  const [isWithdrawPointsOpen, setIsWithdrawPointsOpen] = useState(false);
  const [isPayPalRootOpen, setIsPayPalRootOpen] = useState(false);
  const [isStripeRootOpen, setIsStripeRootOpen] = useState(false);
  const [isBitPayRootOpen, setIsBitPayRootOpen] = useState(false);
  const [isTwoFactorRequiredOpen, setIsTwoFactorRequiredOpen] = useState(false);
  const [isWithdrawalSuspendedOpen, setIsWithdrawalSuspendedOpen] =
    useState(false);

  const availableBalance = useSelector(getAvailableBalance);
  const pendingTransactions = useSelector(getPendingTransactions);
  const totalWithdrawn = useSelector(getTotalWithdrawn);
  const isTwofactorRequired = useSelector(getIsTwofactorRequired);
  const isWithdrawalSuspended = useSelector(getIsWithdrawalSuspended);
  const { items: transactions } = useStream(
    STUDIO_EARNINGS_TRANSACTIONS_STREAM.streamId
  );

  const openTwoFactorRequired = useCallback(
    () => setIsTwoFactorRequiredOpen(true),
    []
  );
  const closeTwoFactorRequired = useCallback(
    () => setIsTwoFactorRequiredOpen(false),
    []
  );
  const openWithdrawalSuspended = useCallback(
    () => setIsWithdrawalSuspendedOpen(true),
    []
  );
  const closeWithdrawalSuspended = useCallback(
    () => setIsWithdrawalSuspendedOpen(false),
    []
  );

  const handleWithdrawalClick = useCallback(
    (paymentMethod: StudioWithdrawalPaymentMethod, onSuccess: () => void) => {
      logEvent(
        PureBiData<StudioEarningsWithdrawalClickBiEvent>({
          evid: 159,
          payment_method: paymentMethod,
          process_step: 'home',
        })
      );

      if (isWithdrawalSuspended) {
        openWithdrawalSuspended();
        return;
      }

      if (isTwofactorRequired) {
        openTwoFactorRequired();
        return;
      }

      onSuccess();
    },
    [
      isWithdrawalSuspended,
      openWithdrawalSuspended,
      isTwofactorRequired,
      openTwoFactorRequired,
      logEvent,
    ]
  );

  const openWithdrawPoints = useCallback(() => {
    handleWithdrawalClick(StudioWithdrawalPaymentMethod.Points, () => {
      setIsWithdrawPointsOpen(true);
    });
  }, [handleWithdrawalClick]);
  const closeWithdrawPoints = useCallback(
    () => setIsWithdrawPointsOpen(false),
    []
  );
  const openPayPalRoot = useCallback(() => {
    handleWithdrawalClick(StudioWithdrawalPaymentMethod.PayPalPayouts, () => {
      setIsPayPalRootOpen(true);
    });
  }, [handleWithdrawalClick]);
  const closePayPalRoot = useCallback(() => setIsPayPalRootOpen(false), []);
  const openStripeRoot = useCallback(() => {
    handleWithdrawalClick(StudioWithdrawalPaymentMethod.Stripe, () => {
      setIsStripeRootOpen(true);
    });
  }, [handleWithdrawalClick]);
  const closeStripeRoot = useCallback(() => setIsStripeRootOpen(false), []);
  const openBitPayRoot = useCallback(() => {
    handleWithdrawalClick(StudioWithdrawalPaymentMethod.BitPay, () => {
      setIsBitPayRootOpen(true);
    });
  }, [handleWithdrawalClick]);
  const closeBitPayRoot = useCallback(() => setIsBitPayRootOpen(false), []);

  useOpenStripeModalFromQueryParam(openStripeRoot);

  const isEmpty = transactions.length === 0;

  if (isEmpty) {
    return (
      <div className={cn(s['root'], className)}>
        <EmptyEarningsList />
      </div>
    );
  }

  return (
    <div className={cn(s['root'], className)}>
      <div className={s['content']}>
        <div className={s['metrics-grid']}>
          <div className={s['metric-card']}>
            <Flex direction="row" justifyContent="space-between">
              <div className={cn(s['metric-icon'], s['available-balance'])}>
                <DollarSign className={s['icon']} size={IconSize.LARGE} />
              </div>
              <div className={s['metric-value']}>
                ${availableBalance || '0.00'}
              </div>
            </Flex>
            <div className={s['metric-content']}>
              <h3 className={s['metric-title']}>
                {t('studio.earnings.availableBalance.title')}
              </h3>
              <p className={s['metric-description']}>
                {t('studio.earnings.availableBalance.description')}
              </p>
            </div>
          </div>

          <div className={s['metric-card']}>
            <Flex direction="row" justifyContent="space-between">
              <div className={cn(s['metric-icon'], s['pending-transactions'])}>
                <StudioScheduled className={s['icon']} size={IconSize.LARGE} />
              </div>
              <div className={s['metric-value']}>
                ${pendingTransactions || '0.00'}
              </div>
            </Flex>
            <div className={s['metric-content']}>
              <h3 className={s['metric-title']}>
                {t('studio.earnings.pendingTransactions.title')}
              </h3>
              <p className={s['metric-description']}>
                {t('studio.earnings.pendingTransactions.description')}
              </p>
            </div>
          </div>

          <div className={s['metric-card']}>
            <Flex direction="row" justifyContent="space-between">
              <div className={cn(s['metric-icon'], s['total-withdrawn'])}>
                <StudioAnalytics className={s['icon']} size={IconSize.LARGE} />
              </div>
              <div className={s['metric-value']}>
                ${totalWithdrawn || '0.00'}
              </div>
            </Flex>
            <div className={s['metric-content']}>
              <h3 className={s['metric-title']}>
                {t('studio.earnings.totalWithdrawn.title')}
              </h3>
              <p className={s['metric-description']}>
                {t('studio.earnings.totalWithdrawn.description')}
              </p>
            </div>
          </div>
        </div>

        <div className={s['withdrawal-section']}>
          <h2 className={s['withdrawal-title']}>
            {t('studio.earnings.withdrawal.title')}
          </h2>
          <div className={s['withdrawal-grid']}>
            <div className="ds-card">
              <div
                className={s['withdrawal-button']}
                onClick={openWithdrawPoints}
              >
                <img
                  src="//st.deviantart.net/eclipse/studio/withdrawal_icons/points.png"
                  alt="Points"
                />
                <span className={s['withdrawal-text']}>
                  {t('studio.earnings.withdrawal.convertToPoints')}
                </span>
              </div>
            </div>
            <div className="ds-card">
              <div className={s['withdrawal-button']} onClick={openPayPalRoot}>
                <img
                  src="//st.deviantart.net/eclipse/studio/withdrawal_icons/paypal.png"
                  alt="PayPal"
                />
                <span className={s['withdrawal-text']}>
                  {t('studio.earnings.withdrawal.withdrawToPayPal')}
                </span>
              </div>
            </div>
            <div className="ds-card">
              <div className={s['withdrawal-button']} onClick={openBitPayRoot}>
                <img
                  src="//st.deviantart.net/eclipse/studio/withdrawal_icons/bitpay.png"
                  alt="BitPay"
                />
                <span className={s['withdrawal-text']}>
                  {t('studio.earnings.withdrawal.withdrawToBitPay')}
                </span>
              </div>
            </div>
            <div className="ds-card">
              <div className={s['withdrawal-button']} onClick={openStripeRoot}>
                <img
                  src="//st.deviantart.net/eclipse/studio/withdrawal_icons/stripe.png"
                  alt="Stripe"
                />
                <span className={s['withdrawal-text']}>
                  {t('studio.earnings.withdrawal.withdrawToStripe')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {isWithdrawPointsOpen && (
          <WithdrawAsPointsModal
            isOpen={isWithdrawPointsOpen}
            requestClose={closeWithdrawPoints}
            availableBalance={parseFloatOrFallback(availableBalance, 0)}
          />
        )}

        <PayPalWithdrawRootModal
          isOpen={isPayPalRootOpen}
          onClose={closePayPalRoot}
        />

        <BitPayWithdrawRootModal
          isOpen={isBitPayRootOpen}
          onClose={closeBitPayRoot}
        />

        <StripeWithdrawRootModal
          isOpen={isStripeRootOpen}
          onClose={closeStripeRoot}
        />

        <TwoFactorRequiredModal
          isOpen={isTwoFactorRequiredOpen}
          requestClose={closeTwoFactorRequired}
        />

        <WithdrawalSuspendedModal
          isOpen={isWithdrawalSuspendedOpen}
          requestClose={closeWithdrawalSuspended}
        />

        <div className={s['transactions-section']}>
          <h2 className={s['transactions-title']}>
            {t('studio.earnings.transactions.title')}
          </h2>
          <TransactionsTable />
        </div>

        <div className={s['info-blocks-section']}>
          <div className={s['info-block']}>
            <h3 className={s['info-block-title']}>
              {t('studio.earnings.infoBlocks.reports.title')}
            </h3>
            <div className={s['info-block-content']}>
              <div className={s['info-item']}>
                <Reports
                  className={s['info-icon']}
                  size={IconSize.SMALL}
                  disableDefaultColors
                />
                <a
                  href={URLS.CUSTOMER_DASHBOARD}
                  className="text-link-level-1-brand f-14-reg"
                >
                  {t('studio.earnings.infoBlocks.reports.customerDashboard')}
                </a>
              </div>
            </div>
          </div>

          <div className={s['info-block']}>
            <div className={s['info-block-header']}>
              <h3 className={s['info-block-title']}>
                {t('studio.earnings.infoBlocks.digitalSalesFees.title')}
              </h3>
            </div>
            <div className={s['info-block-content']}>
              <p className={s['info-block-description']}>
                {t('studio.earnings.infoBlocks.digitalSalesFees.description')}
              </p>
              <a
                href={URLS.DIGITAL_SALES_FEES}
                className="text-link-level-1-brand f-14-reg"
              >
                {t('studio.earnings.infoBlocks.digitalSalesFees.learnMore')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

EarningsPageContent.displayName = 'EarningsPageContent';

export default EarningsPageContent;
